import type { MunicipalDataset } from './parquetDataLoader';
import MunicipalWorker from '../workers/municipalData.worker.ts?worker';

type LoadOptions = {
  targetYear?: number;
  columnsByBundle?: Record<string, string[]>;
};

type WorkerRequest = {
  id: number;
  type: 'load';
  payload?: LoadOptions;
};

type WorkerSuccess = {
  id: number;
  type: 'result';
  payload: MunicipalDataset;
};

type WorkerError = {
  id: number;
  type: 'error';
  error: string;
};

type WorkerResponse = WorkerSuccess | WorkerError;

interface PendingRequest {
  resolve: (value: MunicipalDataset) => void;
  reject: (reason: Error) => void;
  startedAt: number;
  options?: LoadOptions;
}

let worker: Worker | null = null;
const pending = new Map<number, PendingRequest>();
let nextId = 1;

function getWorker(): Worker {
  if (!worker) {
    worker = new MunicipalWorker();
    worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
      const { id, type } = event.data;
      const entry = pending.get(id);
      if (!entry) {
        return;
      }
      pending.delete(id);
      if (type === 'result') {
        const duration = performance.now() - entry.startedAt;
        const year = entry.options?.targetYear ?? 'all';
        const bundles = Object.keys(event.data.payload.loadedColumns ?? {});
        console.debug('[municipal-worker] dataset ready', {
          id,
          year,
          duration: Math.round(duration),
          bundles,
        });
        entry.resolve(event.data.payload);
      } else {
        entry.reject(new Error(event.data.error));
      }
    });
    worker.addEventListener('error', (event) => {
      pending.forEach(({ reject }, id) => {
        reject(new Error(event.message));
      });
      pending.clear();
    });
  }
  return worker;
}

export function loadMunicipalDataset(options?: LoadOptions): Promise<MunicipalDataset> {
  const activeWorker = getWorker();
  const id = nextId++;
  return new Promise((resolve, reject) => {
    pending.set(id, {
      resolve,
      reject,
      startedAt: performance.now(),
      options,
    });
    const message: WorkerRequest = { id, type: 'load', payload: options };
    activeWorker.postMessage(message);
  });
}
